import { UniButton } from '@/design-system/components/buttons';
import { UniIconButton } from '@/design-system/components/buttons';
import { TextBody, TextCaption, TextTitle } from '@/design-system/components/typography';
import { SxProp, View, useDripsyTheme, Pressable, Container, SafeAreaView, ScrollView } from 'dripsy';
import _, { isString } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UniInput } from '@/design-system/components/inputs';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { CreateCardAccountStackParamList, CreateCardAccountStackRoutes } from './type';
import { PageHeaderModalNative } from '../../../account-management/screens/page/PageHeaderModalNative';
import { Dropdown } from '@/design-system/components/dropdown';
import { CardIndividualDocumentTypeSelectFilters } from '../../options';
import { CardAccountStackRoutes } from '../../card.navigator';
import { DocumentUploader, DocumentUploaderProps } from '@/design-system/components/uploadDocument/DocumentUploader';
import * as DocumentPicker from 'expo-document-picker';
import moment from 'moment-timezone';
import { CountrySelector } from '../../components/countrySelector/CountrySelector';
import { useUploadDocument } from '../../hooks/useCardAccounts.hook';
import { UseInputRegularVariant } from '@/design-system/components/inputs/useInput.hook';

export interface CreateCardAccountProps {
  onClose: () => void;
  onLayout: (event: any) => void;
  data: any;
  onData: (data: any) => void;
  func?: (data: any, onSuccess?: (data: any) => void, onError?: (error: any) => void) => void;
  onReturnData?: (data: any) => void;
  refresh?: () => void;
}

export const CreateCardAccountNativeDocumentScreen: React.FC<CreateCardAccountProps> = (props) => {
  const { onClose, onLayout, data, onData, func, onReturnData, refresh } = props;
  const { t } = useTranslation('card-account');
  const { body, textMb, btnView, btn, textTitle, mobileView, mobileCodeView, mobileNumberView, inputSx, account, container } = sxStyles;

  const navigation = useNavigation<NativeStackNavigationProp<CreateCardAccountStackParamList>>();
  const navigationAny = useNavigation<any>();

  const { postUploadDocument } = useUploadDocument({});

  const [cardAccount, setCardAccount] = useState<any>(data);
  const [errorMsg, setErrorMsg] = useState<any>({
    type: undefined,
    front: undefined,
    number: undefined,
    country: undefined,
    expiryDate: undefined,
    back: undefined
  });

  const fileFormats = ['image/png', 'image/jpeg', 'image/gif', 'application/pdf'];

  const validateForm = async (data?: { [key: string]: string | undefined }) => {
    const keys = data ? Object.keys(data) : [];
    const keysLength = keys.length;
    let validateFlag = true;
    let errorMsgFlag = { ...errorMsg };
    let dateReg = /^[1-9]\d{3}-(0[1-9]|1[0-2])-(0[1-9]|[1-2][0-9]|3[0-1])$/;

    if ((!keysLength && !cardAccount?.individual?.document?.type) || (keys.includes('type') && !data?.type)) {
      errorMsgFlag.type = t('card-account::credit-card-new-account.glossary.field-required');
      validateFlag = false;
    } else if ((!keysLength && cardAccount?.individual?.document?.type) || (keys.includes('type') && data?.type)) {
      errorMsgFlag.type = undefined;
    }

    if ((!keysLength && !cardAccount?.individual?.document?.country) || (keys.includes('country') && !data?.country)) {
      errorMsgFlag.country = t('card-account::credit-card-new-account.glossary.field-required');
      validateFlag = false;
    } else if ((!keysLength && cardAccount?.individual?.document?.country) || (keys.includes('country') && data?.country)) {
      errorMsgFlag.country = undefined;
    }

    if (
      ((!keysLength && cardAccount?.individual?.document?.expiryDate) || (keys.includes('expiryDate') && data?.expiryDate)) &&
      !dateReg.test(!keysLength ? cardAccount?.individual?.document?.expiryDate : data?.expiryDate)
    ) {
      errorMsgFlag.expiryDate = t('card-account::credit-card-new-account.glossary.date-format-error');
      validateFlag = false;
    } else {
      errorMsgFlag.expiryDate = undefined;
    }

    if ((!keysLength && !cardAccount?.individual?.document?.number) || (keys.includes('number') && !data?.number)) {
      errorMsgFlag.number = t('card-account::credit-card-new-account.glossary.field-required');
      validateFlag = false;
    } else if ((!keysLength && cardAccount?.individual?.document?.number) || (keys.includes('number') && data?.number)) {
      errorMsgFlag.number = undefined;
    }

    if ((!keysLength && !cardAccount?.individual?.document?.front) || (keys.includes('front') && !data?.front)) {
      errorMsgFlag.front = t('card-account::credit-card-new-account.glossary.field-required');
      validateFlag = false;
    } else if (documentUploaderSelectedDocumentFront) {
      if (documentUploaderSelectedDocumentFront?.mimeType && !fileFormats.includes(documentUploaderSelectedDocumentFront?.mimeType)) {
        errorMsgFlag.front = t('card-account::credit-card-new-account.glossary.accepted-file-format');
        validateFlag = false;
      } else {
        errorMsgFlag.front = undefined;
      }
    }

    if (
      documentUploaderSelectedDocumentBack &&
      documentUploaderSelectedDocumentBack?.mimeType &&
      !fileFormats.includes(documentUploaderSelectedDocumentBack?.mimeType)
    ) {
      errorMsgFlag.back = t('card-account::credit-card-new-account.glossary.accepted-file-format');
      validateFlag = false;
    } else {
      errorMsgFlag.back = undefined;
    }

    setErrorMsg(errorMsgFlag);
    return validateFlag;
  };

  const changeCreateCardAccount = async () => {
    const validate = await validateForm();
    if (validate) {
      onData({ data: cardAccount });
      navigation.navigate(CreateCardAccountStackRoutes.Confirm, {
        onLayout,
        data: cardAccount,
        func,
        onReturnData,
        refresh
      });
    }
  };

  const [documentUploaderSelectedDocumentFront, setDocumentUploaderSelectedDocumentFront] = useState<any>(null);
  const [documentUploaderPropsFront, setDocumentUploaderPropsFront] = useState<DocumentUploaderProps>({
    intructions: t('card-account::credit-card-new-account.glossary.upload-front'),
    onValueChange: (document: undefined | DocumentPicker.DocumentResult) => {
      setDocumentUploaderSelectedDocumentFront(document);
    },
    isLoading: undefined,
    isUploadSuccess: undefined
  });
  const handleChangeUniUploaderPropsFront = useCallback(
    (key: keyof DocumentUploaderProps, value: DocumentUploaderProps[keyof DocumentUploaderProps]) => {
      setDocumentUploaderPropsFront((prev) => ({ ...prev, [key]: value }));
    },
    []
  );
  useEffect(() => {
    if (!documentUploaderSelectedDocumentFront) return;
    if (!fileFormats.includes(documentUploaderSelectedDocumentFront?.mimeType)) {
      let cardAccountData = cardAccount;
      cardAccountData.individual.document = { ...cardAccount.individual.document, front: undefined };
      setCardAccount({
        ...cardAccountData
      });
      setErrorMsg({
        ...errorMsg,
        front: t('card-account::credit-card-new-account.glossary.accepted-file-format')
      });
      return;
    }
    if (documentUploaderSelectedDocumentFront && documentUploaderSelectedDocumentFront?.type == 'success') {
      let cardAccountData = cardAccount;
      cardAccountData.individual.document = { ...cardAccount.individual.document, front: documentUploaderSelectedDocumentFront?.uri };
      setCardAccount({
        ...cardAccountData
      });
      setErrorMsg({
        ...errorMsg,
        front: undefined
      });
    }
    handleChangeUniUploaderPropsFront('intructions', t('card-account::credit-card-new-account.glossary.upload-again'));
  }, [documentUploaderSelectedDocumentFront]);

  const [documentUploaderSelectedDocumentBack, setDocumentUploaderSelectedDocumentBack] = useState<any>(null);
  const [documentUploaderPropsBack, setDocumentUploaderPropsBack] = useState<DocumentUploaderProps>({
    intructions: t('card-account::credit-card-new-account.glossary.upload-back'),
    onValueChange: (document: undefined | DocumentPicker.DocumentResult) => {
      setDocumentUploaderSelectedDocumentBack(document);
    },
    isLoading: undefined,
    isUploadSuccess: undefined
  });
  const handleChangeUniUploaderPropsBack = useCallback(
    (key: keyof DocumentUploaderProps, value: DocumentUploaderProps[keyof DocumentUploaderProps]) => {
      setDocumentUploaderPropsBack((prev) => ({ ...prev, [key]: value }));
    },
    []
  );
  useEffect(() => {
    if (!documentUploaderSelectedDocumentBack) return;
    if (!fileFormats.includes(documentUploaderSelectedDocumentBack?.mimeType)) {
      let cardAccountData = cardAccount;
      cardAccountData.individual.document = { ...cardAccount.individual.document, back: undefined };
      setCardAccount({
        ...cardAccountData
      });
      setErrorMsg({
        ...errorMsg,
        back: t('card-account::credit-card-new-account.glossary.accepted-file-format')
      });
      return;
    }
    if (documentUploaderPropsBack && documentUploaderSelectedDocumentBack?.type == 'success') {
      let cardAccountData = cardAccount;
      cardAccountData.individual.document = { ...cardAccount.individual.document, back: documentUploaderSelectedDocumentBack?.uri };
      setCardAccount({
        ...cardAccountData
      });
      setErrorMsg({
        ...errorMsg,
        back: undefined
      });
    }
    handleChangeUniUploaderPropsBack('intructions', t('card-account::credit-card-new-account.glossary.upload-again'));
  }, [documentUploaderSelectedDocumentBack]);

  const { theme } = useDripsyTheme();

  return (
    <SafeAreaView sx={container}>
      <ScrollView showsVerticalScrollIndicator={false}>
        <View sx={{ backgroundColor: '$backgroundPrimary', width: '100%' }}>
          <PageHeaderModalNative>
            <View sx={{ px: '$4', mt: '$5', mb: '$4' }}>
              <View sx={{ position: 'absolute', top: 0, left: 12, zIndex: 2 }}>
                <UniIconButton
                  icon="arrowLeft"
                  options={{ iconStyles: { color: theme?.colors?.$white as string } }}
                  onPress={() => {
                    onClose();
                    onData(cardAccount);
                  }}></UniIconButton>
              </View>
              <View sx={{ textAlign: 'center' }}>
                <TextTitle bold sx={{ color: theme?.colors?.$white as string }}>
                  {t('card-account::credit-card-new-account.glossary.new-account')}
                </TextTitle>
              </View>
            </View>
          </PageHeaderModalNative>
          <View sx={body}>
            <TextTitle sx={textMb} semiBold>
              {t('card-account::credit-card-new-account.new-account-page.text--fill-out-to-create-account')}
            </TextTitle>
            <View sx={{ gap: '$5' }}>
              <View sx={{ gap: '$1', zIndex: 99 }}>
                <Dropdown
                  value={cardAccount?.individual?.document?.type}
                  label={t('card-account::credit-card-new-account.glossary.identificaiton-document-type')}
                  options={CardIndividualDocumentTypeSelectFilters(t)}
                  sx={{ width: '100%' }}
                  onValueChange={(value) => {
                    let cardAccountData = cardAccount;
                    cardAccountData.individual.document = { ...cardAccount.individual.document, type: value };
                    setCardAccount({
                      ...cardAccountData
                    });
                    validateForm({ type: isString(value) ? value : '' });
                  }}
                />
                {errorMsg?.type && (
                  <TextCaption danger sx={{ mt: '$1' }}>
                    {errorMsg?.type}
                  </TextCaption>
                )}
              </View>
              <View sx={{ gap: 4, zIndex: 98 }}>
                <TextCaption semiBold>{t('card-account::credit-card-new-account.glossary.country-region')}</TextCaption>
                <CountrySelector
                  value={cardAccount?.individual?.document?.country}
                  onValueChange={(value) => {
                    let cardAccountData = cardAccount;
                    cardAccountData.individual.document = { ...cardAccount.individual.document, country: value };
                    setCardAccount({
                      ...cardAccountData
                    });
                    validateForm({ country: value });
                  }}
                />
                {errorMsg?.country && (
                  <TextCaption danger sx={{ mt: '$1' }}>
                    {errorMsg?.country}
                  </TextCaption>
                )}
              </View>
              <UniInput
                regular={UseInputRegularVariant.date}
                value={cardAccount?.individual?.document?.expiryDate}
                labelText={t('card-account::credit-card-new-account.glossary.expiry-date')}
                errorText={errorMsg.expiryDate}
                onValueChange={(value) => {
                  let cardAccountData = cardAccount;
                  cardAccountData.individual.document = { ...cardAccount.individual.document, expiryDate: value };
                  setCardAccount({
                    ...cardAccountData
                  });
                  validateForm({ expiryDate: value });
                }}
                placeholder={'yyyy-mm-dd'}
              />
              <UniInput
                value={cardAccount?.individual?.document?.number}
                labelText={t('card-account::credit-card-new-account.glossary.id-passport-license-number')}
                errorText={errorMsg.number}
                onValueChange={(value) => {
                  let cardAccountData = cardAccount;
                  cardAccountData.individual.document = { ...cardAccount.individual.document, number: value };
                  setCardAccount({
                    ...cardAccountData
                  });
                  validateForm({ number: value });
                }}
              />
              <Container sx={{ gap: '$1' }}>
                <DocumentUploader
                  title={t('card-account::credit-card-new-account.glossary.front')}
                  intructions={documentUploaderPropsFront.intructions}
                  onValueChange={setDocumentUploaderSelectedDocumentFront}
                  isLoading={documentUploaderPropsFront.isLoading}
                  isUploadSuccess={documentUploaderSelectedDocumentFront && cardAccount?.individual?.document?.front ? true : false}
                />
                {errorMsg?.front && (
                  <TextCaption danger sx={{ mt: '$1' }}>
                    {errorMsg?.front}
                  </TextCaption>
                )}
              </Container>
              <Container>
                <DocumentUploader
                  title={t('card-account::credit-card-new-account.glossary.back')}
                  intructions={documentUploaderPropsBack.intructions}
                  onValueChange={setDocumentUploaderSelectedDocumentBack}
                  isLoading={documentUploaderPropsBack.isLoading}
                  isUploadSuccess={documentUploaderSelectedDocumentBack && cardAccount?.individual?.document?.back ? true : false}
                />
                {errorMsg?.back && (
                  <TextCaption danger sx={{ mt: '$1' }}>
                    {errorMsg?.back}
                  </TextCaption>
                )}
              </Container>
            </View>
            <View sx={btnView}>
              <UniButton
                sx={btn}
                labelSx={{ color: '$balck' }}
                label={t('card-account::credit-card-new-account.glossary.submit')}
                onPress={changeCreateCardAccount}
              />
            </View>
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const sxStyles: Record<string, SxProp> = {
  container: {
    height: '100%',
    width: '100%',
    backgroundColor: '$backgroundPrimary',
    zIndex: -100
  },
  body: {
    px: 16,
    py: 20
  },
  textMb: {
    marginBottom: 20
  },
  btnView: {
    width: '100%',
    left: 0,
    bottom: 0,
    pt: '$10'
  },
  btn: {
    width: '100%',
    height: 44
  },
  textTitle: {
    marginBottom: '$1'
  },
  mobileView: {
    flexDirection: 'row'
  },
  mobileCodeView: {
    width: '30%',
    mr: '$2'
  },
  inputSx: {
    width: '100%'
  },
  mobileNumberView: {
    width: '68%'
  },
  account: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'row',
    py: '$5',
    px: '$4',
    borderWidth: 1,
    borderColor: '#DCDCDC',
    borderRadius: 5
  }
};
